import { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import supabase from '../utils/Supabase';
import {
    FeaturedScriptRow,
    getFeaturedAdminList,
    removeFeaturedScript,
    updateFeaturedScript,
    slugify,
    upsertFeaturedScript,
} from '../utils/Featured';
import { showToast } from '../utils/Toasts';

import { LoadingRing } from '../components/LoadingRing';

type FeaturedRow = FeaturedScriptRow & { scriptTitle: string | null };
type SearchResult = { file_id: string; title: string; writers: string | null; details: string | null };

// A title search is a substring match, so a common word ("Scream") matches far more
// rows than anyone wants to scroll past. Cap the list and say how many were left
// out, and run a second pass for titles that *start* with the term so the obvious
// match can never fall off the end of a truncated page.
const SEARCH_LIMIT = 100;
const SEARCH_COLUMNS = 'file_id, "Script Title", "Writer(s)", "Year", additional_info_concat';

const escapeLike = (term: string) => term.replace(/[\\%_]/g, (match) => `\\${match}`);

const escapeRegExp = (term: string) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 0 = the exact title, 1 = starts with the term, 2 = term starts a word, 3 = anywhere. */
const rankMatch = (title: string, term: string) => {
    const haystack = title.trim().toLowerCase();
    const needle = term.trim().toLowerCase();

    if (haystack === needle) return 0;
    if (haystack.startsWith(needle)) return 1;

    return new RegExp(`\\b${escapeRegExp(needle)}`).test(haystack) ? 2 : 3;
};

const emptyDraft = {
    file_id: '',
    slug: '',
    headline: '',
    blurb: '',
    allow_download: false,
    admin_only: true,
    is_active: true,
};

/**
 * Curate the scripts that are readable without an account. Adding a row here is
 * the only way a script becomes public, so the list is deliberately small and
 * every field is explicit.
 */
export default function AdminFeatured() {
    const [rows, setRows] = useState<FeaturedRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [searched, setSearched] = useState(false);
    const [searching, setSearching] = useState(false);

    const [draft, setDraft] = useState({ ...emptyDraft });
    const [draftTitle, setDraftTitle] = useState('');

    const load = async () => {
        setLoading(true);
        setRows(await getFeaturedAdminList());
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const runSearch = async () => {
        const term = search.trim();
        if (term.length < 3) {
            showToast('info', 'Enter at least 3 characters to search.');
            return;
        }

        setSearching(true);

        const pattern = escapeLike(term);

        // Any signed-in member can read "files", so no edge function needed here.
        const [prefixMatches, anyMatches] = await Promise.all([
            supabase
                .from('files')
                .select(SEARCH_COLUMNS)
                .ilike('Script Title', `${pattern}%`)
                .eq('record_deleted', false)
                .order('Script Title', { ascending: true })
                .limit(SEARCH_LIMIT),
            supabase
                .from('files')
                .select(SEARCH_COLUMNS, { count: 'exact' })
                .ilike('Script Title', `%${pattern}%`)
                .eq('record_deleted', false)
                .order('Script Title', { ascending: true })
                .limit(SEARCH_LIMIT),
        ]);

        setSearching(false);
        setSearched(true);

        const error = prefixMatches.error ?? anyMatches.error;

        if (error) {
            showToast('error', `Unable to search scripts: ${error.message}`);
            return;
        }

        const byFileId = new Map<string, SearchResult>();

        for (const row of [...(prefixMatches.data ?? []), ...(anyMatches.data ?? [])] as any[]) {
            if (byFileId.has(row.file_id)) continue;

            byFileId.set(row.file_id, {
                file_id: row.file_id,
                title: row['Script Title'] ?? '(untitled)',
                writers: row['Writer(s)'] ?? null,
                // Several drafts of one film share a title, so show what tells them apart.
                details: [row['Year'], row.additional_info_concat].filter(Boolean).join(' · ') || null,
            });
        }

        const found = [...byFileId.values()].sort(
            (a, b) => rankMatch(a.title, term) - rankMatch(b.title, term) || a.title.localeCompare(b.title)
        );

        setResults(found);
        setTotalMatches(Math.max(anyMatches.count ?? found.length, found.length));
    };

    const selectScript = (result: SearchResult) => {
        setDraft({
            ...emptyDraft,
            file_id: result.file_id,
            slug: slugify(result.title),
            headline: result.title,
        });
        setDraftTitle(result.title);
        setResults([]);
        setTotalMatches(0);
        setSearched(false);
        setSearch('');
    };

    const editRow = (row: FeaturedRow) => {
        setDraft({
            file_id: row.file_id,
            slug: row.slug,
            headline: row.headline ?? '',
            blurb: row.blurb ?? '',
            allow_download: row.allow_download,
            admin_only: row.admin_only,
            is_active: row.is_active,
        });
        setDraftTitle(row.scriptTitle ?? '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const save = async () => {
        if (!draft.file_id || !draft.slug) {
            showToast('error', 'Pick a script and give it a URL slug.');
            return;
        }

        if (draft.slug !== slugify(draft.slug)) {
            showToast('error', 'The slug may only contain lowercase letters, numbers and single hyphens.');
            return;
        }

        setSaving(true);
        const errorMessage = await upsertFeaturedScript({
            file_id: draft.file_id,
            slug: draft.slug,
            headline: draft.headline.trim() || null,
            blurb: draft.blurb.trim() || null,
            allow_download: draft.allow_download,
            admin_only: draft.admin_only,
            is_active: draft.is_active,
        });
        setSaving(false);

        if (errorMessage) {
            showToast('error', `Unable to save: ${errorMessage}`);
            return;
        }

        showToast('success', 'Featured script saved.');
        setDraft({ ...emptyDraft });
        setDraftTitle('');
        load();
    };

    const toggle = async (row: FeaturedRow, field: 'is_active' | 'allow_download' | 'admin_only') => {
        const errorMessage = await updateFeaturedScript(row.file_id, { [field]: !row[field] });

        if (errorMessage) {
            showToast('error', `Unable to update: ${errorMessage}`);
            return;
        }

        load();
    };

    const remove = async (row: FeaturedRow) => {
        if (!window.confirm(`Stop featuring "${row.headline || row.scriptTitle}"? The public link will stop working.`)) {
            return;
        }

        if (await removeFeaturedScript(row.file_id)) {
            showToast('success', 'Removed from featured scripts.');
            load();
        } else {
            showToast('error', 'Unable to remove that featured script.');
        }
    };

    return (
        <>
            <Row className="mb-3">
                <Col xs={12}>
                    <p className="text-muted mb-1">
                        Featured scripts are readable by anyone, with no account and no login prompt, at{' '}
                        <code>/featured/&lt;slug&gt;</code>. Everything else in the library stays behind an
                        account.
                    </p>
                    <p className="text-muted mb-1">
                        A <strong>Staged</strong> script is visible to approvers only — to everyone else it
                        behaves as if it were never featured at all, so you can check it on the live site
                        before launch. Switch it to <strong>Public</strong> when you're ready to share the link.
                    </p>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col xs={12}>
                    <h3 className="fs-5">{draft.file_id ? 'Edit featured script' : 'Feature a script'}</h3>
                </Col>

                {!draft.file_id && (
                    <Col xs={12}>
                        <Form
                            className="d-flex gap-2 flex-wrap mb-2"
                            onSubmit={(e) => {
                                e.preventDefault();
                                runSearch();
                            }}
                        >
                            <Form.Control
                                type="text"
                                placeholder="Search scripts by title…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{ maxWidth: '28rem' }}
                            />
                            <Button type="submit" variant="secondary" disabled={searching}>
                                {searching ? 'Searching…' : 'Search'}
                            </Button>
                        </Form>

                        {searched && !searching && results.length === 0 && (
                            <p className="text-muted small mb-0">No scripts match that title.</p>
                        )}

                        {results.length > 0 && (
                            <>
                                <p className="text-muted small mb-1">
                                    {totalMatches > results.length
                                        ? `Closest ${results.length} of ${totalMatches} matching titles — type more of the title to narrow it down.`
                                        : `${totalMatches} matching title${totalMatches === 1 ? '' : 's'}.`}
                                </p>

                                <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                                    <Table size="sm" hover responsive className="align-middle">
                                        <tbody>
                                            {results.map((result) => (
                                                <tr key={result.file_id}>
                                                    <td>
                                                        <div>{result.title}</div>
                                                        {result.details && (
                                                            <div className="text-muted small">{result.details}</div>
                                                        )}
                                                    </td>
                                                    <td className="text-muted">{result.writers}</td>
                                                    <td className="text-end">
                                                        <Button
                                                            size="sm"
                                                            variant="primary"
                                                            onClick={() => selectScript(result)}
                                                        >
                                                            Select
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </>
                        )}
                    </Col>
                )}

                {draft.file_id && (
                    <Col xs={12}>
                        <Form>
                            <Row className="g-3">
                                <Col md={6} xs={12}>
                                    <Form.Label className="small fw-semibold mb-1">Script</Form.Label>
                                    <Form.Control type="text" value={draftTitle || draft.file_id} readOnly />
                                </Col>
                                <Col md={6} xs={12}>
                                    <Form.Label className="small fw-semibold mb-1">URL slug</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={draft.slug}
                                        onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                                    />
                                    <Form.Text className="text-muted">/featured/{draft.slug || '…'}</Form.Text>
                                </Col>
                                <Col md={6} xs={12}>
                                    <Form.Label className="small fw-semibold mb-1">Headline</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Defaults to the script title"
                                        value={draft.headline}
                                        onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
                                    />
                                </Col>
                                <Col md={6} xs={12}>
                                    <Form.Label className="small fw-semibold mb-1">Blurb</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Optional context, e.g. a dedication or the article this accompanies."
                                        value={draft.blurb}
                                        onChange={(e) => setDraft({ ...draft, blurb: e.target.value })}
                                    />
                                </Col>
                                <Col xs={12} className="d-flex gap-4 flex-wrap">
                                    <Form.Check
                                        type="switch"
                                        id="featured_is_active"
                                        label="Live"
                                        checked={draft.is_active}
                                        onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="featured_allow_download"
                                        label="Allow anonymous downloads"
                                        checked={draft.allow_download}
                                        onChange={(e) => setDraft({ ...draft, allow_download: e.target.checked })}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="featured_admin_only"
                                        label="Admin only (staged, not public yet)"
                                        checked={draft.admin_only}
                                        onChange={(e) => setDraft({ ...draft, admin_only: e.target.checked })}
                                    />
                                </Col>
                                <Col xs={12} className="d-flex gap-2">
                                    <Button variant="primary" onClick={save} disabled={saving}>
                                        {saving ? 'Saving…' : 'Save'}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setDraft({ ...emptyDraft });
                                            setDraftTitle('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                )}
            </Row>

            <Row>
                <Col xs={12}>
                    <h3 className="fs-5">Currently featured</h3>
                    {loading ? (
                        <LoadingRing />
                    ) : rows.length === 0 ? (
                        <p className="text-muted">No scripts are featured yet.</p>
                    ) : (
                        <Table hover responsive className="align-middle">
                            <thead>
                                <tr>
                                    <th>Script</th>
                                    <th>Public link</th>
                                    <th>Live</th>
                                    <th>Downloads</th>
                                    <th>Visibility</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.file_id}>
                                        <td className="text-break">
                                            {row.headline || row.scriptTitle}
                                            {row.headline && row.scriptTitle && row.headline !== row.scriptTitle && (
                                                <div className="text-muted small">{row.scriptTitle}</div>
                                            )}
                                        </td>
                                        <td>
                                            <Link to={`/featured/${row.slug}`} target="_blank">
                                                /featured/{row.slug}
                                            </Link>
                                        </td>
                                        <td>
                                            <Form.Check
                                                type="switch"
                                                id={`active_${row.file_id}`}
                                                checked={row.is_active}
                                                onChange={() => toggle(row, 'is_active')}
                                            />
                                        </td>
                                        <td>
                                            <Form.Check
                                                type="switch"
                                                id={`download_${row.file_id}`}
                                                checked={row.allow_download}
                                                onChange={() => toggle(row, 'allow_download')}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant={row.admin_only ? 'outline-warning' : 'outline-success'}
                                                onClick={() => toggle(row, 'admin_only')}
                                                title={row.admin_only
                                                    ? 'Only approvers can see this. Click to publish.'
                                                    : 'Anyone can see this. Click to unpublish.'}
                                            >
                                                {row.admin_only ? 'Staged' : 'Public'}
                                            </Button>
                                        </td>
                                        <td className="text-end">
                                            <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => editRow(row)}>
                                                Edit
                                            </Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => remove(row)}>
                                                Remove
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Col>
            </Row>
        </>
    );
}
